import { useContext } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';

import { Box } from '../../Box';
import { InputLabelContext } from '../../shared/InputLabelContext';
import { Text } from '../../Text';
import type { TextProps } from '../../Text';

const { formLayout: formLayoutTokens } = tokens;

export type TitleProps = Partial<Pick<TextProps, 'tag'>> & {
  /**
   * The headline of the form
   */
  title: string;
  /**
   * Optional prop to add a message that either all fields are required (true)
   * or all fields are optional (false)
   */
  fieldsRequired?: boolean;
  /**
   * Optional subhead of the form
   */
  subhead?: React.ReactNode;
};

/**
 * Displays the title for form pages. You can also include subhead copy and, if
 * you pass `fieldsRequired`, a message that all fields are required or
 * optional. If you pass `singleQuestionInputId` to the `<FormLayout>`
 * component, the title will be used as the input's label.
 */
export const Title = ({ tag, title, fieldsRequired, subhead }: TitleProps) => {
  const { inputId } = useContext(InputLabelContext);

  return (
    <Box
      gap={formLayoutTokens.padding.questions.titleText.smallViewport}
      $group-formLayout-largerThanSM={{
        gap: formLayoutTokens.padding.questions.titleText.largeMediumViewport,
        flexBasis: '50%',
        flexShrink: 1,
      }}
    >
      <Text
        tag={inputId ? 'label' : tag ? tag : 'h1'}
        htmlFor={inputId}
        color={formLayoutTokens.color.content.singleQuestion.title}
        typography={formLayoutTokens.typography.singleQuestion.title.smallViewport}
        $group-formLayout-largerThanSM={{
          typography: formLayoutTokens.typography.singleQuestion.title.largeMediumViewports,
        }}
      >
        {title}
      </Text>
      {typeof fieldsRequired === 'boolean' && (
        <Text
          tag="span"
          testID="fieldsRequired"
          color={formLayoutTokens.color.content.singleQuestion.supportText}
          typography={formLayoutTokens.typography.singleQuestion.requirement.smallXsmallViewport}
          $group-formLayout-largerThanSM={{
            typography: formLayoutTokens.typography.singleQuestion.requirement.largeMediumViewport,
          }}
        >
          {fieldsRequired ? 'All fields are required' : 'All fields are optional'}
        </Text>
      )}
      {typeof subhead === 'string' ? (
        <Text
          tag="span"
          testID="subhead"
          color={formLayoutTokens.color.content.singleQuestion.supportText}
          typography={formLayoutTokens.typography.singleQuestion.supportText.smallViewport}
          $group-formLayout-largerThanSM={{
            typography: formLayoutTokens.typography.singleQuestion.supportText.largeMediumViewports,
          }}
        >
          {subhead}
        </Text>
      ) : (
        subhead
      )}
    </Box>
  );
};
